// utils/csvExportUtils.ts

import Papa from 'papaparse';
import * as R from 'ramda';
import * as format from '@app/utils/formatters';
import downloadCSV from '@app/utils/downloadCsv';
import sportsApi from '@app/utils/sportsApi';
import getUseRightsholderAttribution from '@app/utils/getUseRightsholderAttribution';
import { Notification } from '@app/components/notifications/Notifications';

/** API returns single "crossfit", multiple "a,b", or null. Normalize to CSV string. */
function formatCollaboratingAccountsForCsv(
    value: string | string[] | null | undefined,
): string {
    if (value == null) return '';
    if (Array.isArray(value)) return value.filter(Boolean).join(',');
    const trimmed = String(value).trim();
    return trimmed;
}

function getAuthorshipTypeLabel(
    value: string | null | undefined,
    t: (key: string, opts?: { ns?: string }) => string,
): string {
    if (value == null || value === '') return '';
    const key =
        value === 'standard'
            ? 'filters.authorshipType.standard'
            : value === 'collaboration'
              ? 'filters.authorshipType.collaboration'
              : value === 'unsupported'
                ? 'filters.authorshipType.unsupported'
                : null;
    return key ? t(key, { ns: 'common' }) : value;
}

export interface ExportCsvParams {
    media: any;
    filters: any;
    query: any;
    client: any;
    tableColumns: any;
    user: any;
    openCSVDownloadModal: () => void;
    closeCSVDownloadModal: () => void;
    getFilters: (filters: any) => Record<string, any>;
    sendNotification: (text: string, configs?: Notification['options']) => void;
    t: (key: string, options?: { ns?: string }) => string;
}

export const exportCsv = async ({
    media,
    filters,
    query,
    client,
    tableColumns,
    user,
    openCSVDownloadModal,
    closeCSVDownloadModal,
    getFilters,
    sendNotification,
    t,
}: ExportCsvParams) => {
    const openCSVDownloadModalTimeout = setTimeout(() => {
        openCSVDownloadModal();
    }, 0);

    try {
        const overrides = media.filtersOverride || {};
        const globFilters = getFilters(filters);
        const mvpScoreGreaterThan = filters.mvpScore;
        const hasRedesignedFilters =
            client.permissions.includes('FILTER_REDESIGN');
        const cleanParams = R.omit(['sort', 'order'], query);
        const useRightsholderAttribution = getUseRightsholderAttribution(
            client,
            client.id,
        );

        const payload = {
            action: 'fetchAllQuery',
            domainObject: {
                ...cleanParams,
                groupCarouselPosts: true,
                avgMvpScoreGreaterThan: hasRedesignedFilters
                    ? parseFloat(mvpScoreGreaterThan)
                    : parseFloat(cleanParams.avgMvpScoreGreaterThan),
                includeTextTypeExposures: true,
                includeTextMediaTypes: true,
                ...globFilters,
                ...overrides,
                page: 0,
                pageSize: null,
                ...(useRightsholderAttribution
                    ? { useRightsholderAttribution: true }
                    : {}),
            },
        };

        const response = await sportsApi
            .url('/social-posts')
            .json(payload)
            .post()
            .json();

        const allPosts = (response as { posts: any[] }).posts;
        const currency = client?.currency?.code;

        if (!allPosts) {
            throw new Error('An error occurred while fetching posts.');
        }

        const postsDataById: Record<string, any> = {};
        const {
            postLevelMetrics,
            organicAndPaidMetrics,
            showCollabPostInfo = false,
        } = tableColumns;
        const { csvDelimiter: delimiter } = user;
        const includeCollabColumns = showCollabPostInfo;

        const exposuresData = allPosts.reduce((acc, post) => {
            const {
                platformPostId: platformPostID,
                id: postId,
                postUrl: url,
                createdAt: date,
                socialPlatform: platform,
                hashtags: ht,
                mediaType: { name: mediaType },
                socialAccount: { username: account },
                sponsorAnnotationsDetails: annotations,
                fullMediaValue: FMV,
                impressions,
                videoViews,
                engagements,
                organicImpressions,
                organicVideoViews,
                organicEngagements,
                organicFullMediaValue,
                organicSponsorMediaValue,
                paidImpressions,
                paidVideoViews,
                paidEngagements,
                paidFullMediaValue,
                paidSponsorMediaValue,
                authorshipType,
                collaboratingAccounts,
            } = post;

            const hashtags = ht.length
                ? ht.reduce((acc, hash, i, src) => {
                      const [, tag] = hash.split('#');
                      acc += i < src.length - 1 ? `#${tag} ` : `#${tag}`;
                      return acc;
                  }, '')
                : null;

            const rows = annotations.reduce((accRows: any[], ant: any) => {
                const {
                    accountOwnershipType: { name: accountOwnershipType },
                    mvpScore: MVP,
                    rightsholder: { name: rightholder },
                    sponsor: { name: sponsor },
                    placement: { name: placement },
                    sponsorMediaValue: SMV,
                    fullMediaValue: exposureFmv,
                    organicFullMediaValue: exposureOrganicFmv,
                    paidFullMediaValue: exposurePaidFmv,
                } = ant;

                const newRow: Record<string, any> = {
                    [t('ownedSocial:socialPostMetrics.columns.platformPostId')]:
                        platformPostID,
                    [t('ownedSocial:socialPostMetrics.columns.url')]: url,
                    [t('ownedSocial:socialPostMetrics.columns.date')]:
                        format.shortDate(date),
                    [t('ownedSocial:socialPostMetrics.columns.platform')]:
                        platform,
                    [t('ownedSocial:socialPostMetrics.columns.hashtags')]:
                        hashtags,
                    [t('ownedSocial:socialPostMetrics.columns.mediaType')]:
                        mediaType,
                    [t('ownedSocial:socialPostMetrics.columns.account')]:
                        account,
                    [t(
                        'ownedSocial:socialPostMetrics.columns.accountOwnershipType',
                    )]: accountOwnershipType,
                    [t('ownedSocial:socialPostMetrics.columns.rightsholder')]:
                        rightholder,
                    [t('ownedSocial:socialPostMetrics.columns.placement')]:
                        placement,
                    [t('ownedSocial:socialPostMetrics.columns.sponsor')]:
                        sponsor,
                };

                if (includeCollabColumns) {
                    newRow[
                        t('socialMediaExposure.table.authorshipType', {
                            ns: 'measurement',
                        })
                    ] = getAuthorshipTypeLabel(authorshipType, t);
                    newRow[
                        t('socialMediaExposure.table.collaboratingAccounts', {
                            ns: 'measurement',
                        })
                    ] = formatCollaboratingAccountsForCsv(
                        collaboratingAccounts,
                    );
                }

                if (
                    (!organicAndPaidMetrics && postLevelMetrics) ||
                    (!organicAndPaidMetrics && !postLevelMetrics)
                ) {
                    newRow[
                        t('ownedSocial:socialPostMetrics.columns.impressions')
                    ] = format.rawNumber(impressions);
                    newRow[
                        t('ownedSocial:socialPostMetrics.columns.videoViews')
                    ] = format.rawNumber(videoViews);
                    newRow[
                        t('ownedSocial:socialPostMetrics.columns.engagements')
                    ] = format.rawNumber(engagements);
                }

                if (organicAndPaidMetrics) {
                    newRow[
                        t(
                            'ownedSocial:socialPostMetrics.columns.organicImpressions',
                        )
                    ] = format.rawNumber(organicImpressions);
                    newRow[
                        t(
                            'ownedSocial:socialPostMetrics.columns.organicVideoViews',
                        )
                    ] = format.rawNumber(organicVideoViews);
                    newRow[
                        t(
                            'ownedSocial:socialPostMetrics.columns.organicEngagements',
                        )
                    ] = format.rawNumber(organicEngagements);
                    newRow[
                        `${t('ownedSocial:socialPostMetrics.columns.organicFullMediaValue')}-${currency}`
                    ] = format.rawNumber(exposureOrganicFmv);
                    newRow[
                        `${t('ownedSocial:socialPostMetrics.columns.organicSponsorMediaValue')}-${currency}`
                    ] = format.rawNumber(organicSponsorMediaValue);
                    newRow[
                        t(
                            'ownedSocial:socialPostMetrics.columns.paidImpressions',
                        )
                    ] = format.rawNumber(paidImpressions);
                    newRow[
                        t(
                            'ownedSocial:socialPostMetrics.columns.paidVideoViews',
                        )
                    ] = format.rawNumber(paidVideoViews);
                    newRow[
                        t(
                            'ownedSocial:socialPostMetrics.columns.paidEngagements',
                        )
                    ] = format.rawNumber(paidEngagements);
                    newRow[
                        `${t('ownedSocial:socialPostMetrics.columns.paidFullMediaValue')}-${currency}`
                    ] = format.rawNumber(exposurePaidFmv);
                    newRow[
                        `${t('ownedSocial:socialPostMetrics.columns.paidSponsorMediaValue')}-${currency}`
                    ] = format.rawNumber(paidSponsorMediaValue);
                }

                newRow[t('ownedSocial:socialPostMetrics.columns.mvp')] =
                    format.rawNumber(MVP);
                newRow[
                    `${t('ownedSocial:socialPostMetrics.columns.fullMediaValue')}-${currency}`
                ] = format.rawNumber(exposureFmv);
                newRow[
                    `${t('ownedSocial:socialPostMetrics.columns.sponsorMediaValue')}-${currency}`
                ] = format.rawNumber(SMV);

                accRows.unshift(newRow);

                if (!postsDataById[postId]) {
                    // Store with translated keys for CSV export
                    const postRow: Record<string, any> = {
                        [t(
                            'ownedSocial:socialPostMetrics.columns.platformPostId',
                        )]: platformPostID,
                        [t('ownedSocial:socialPostMetrics.columns.url')]: url,
                        [t('ownedSocial:socialPostMetrics.columns.date')]:
                            format.shortDate(date),
                        [t('ownedSocial:socialPostMetrics.columns.platform')]:
                            platform,
                        [t('ownedSocial:socialPostMetrics.columns.hashtags')]:
                            hashtags,
                        [t('ownedSocial:socialPostMetrics.columns.mediaType')]:
                            mediaType,
                        [t('ownedSocial:socialPostMetrics.columns.account')]:
                            account,
                    };

                    if (includeCollabColumns) {
                        postRow[
                            t('socialMediaExposure.table.authorshipType', {
                                ns: 'measurement',
                            })
                        ] = getAuthorshipTypeLabel(authorshipType, t);
                        postRow[
                            t(
                                'socialMediaExposure.table.collaboratingAccounts',
                                {
                                    ns: 'measurement',
                                },
                            )
                        ] = formatCollaboratingAccountsForCsv(
                            collaboratingAccounts,
                        );
                    }

                    if (
                        (!organicAndPaidMetrics && postLevelMetrics) ||
                        (!organicAndPaidMetrics && !postLevelMetrics)
                    ) {
                        postRow[
                            t(
                                'ownedSocial:socialPostMetrics.columns.impressions',
                            )
                        ] = format.rawNumber(impressions);
                        postRow[
                            t(
                                'ownedSocial:socialPostMetrics.columns.videoViews',
                            )
                        ] = format.rawNumber(videoViews);
                        postRow[
                            t(
                                'ownedSocial:socialPostMetrics.columns.engagements',
                            )
                        ] = format.rawNumber(engagements);
                    }

                    if (organicAndPaidMetrics) {
                        postRow[
                            t(
                                'ownedSocial:socialPostMetrics.columns.organicImpressions',
                            )
                        ] = format.rawNumber(organicImpressions);
                        postRow[
                            t(
                                'ownedSocial:socialPostMetrics.columns.organicVideoViews',
                            )
                        ] = format.rawNumber(organicVideoViews);
                        postRow[
                            t(
                                'ownedSocial:socialPostMetrics.columns.organicEngagements',
                            )
                        ] = format.rawNumber(organicEngagements);
                        postRow[
                            `${t('ownedSocial:socialPostMetrics.columns.organicFullMediaValue')}-${currency}`
                        ] = format.rawNumber(organicFullMediaValue);
                        postRow[
                            t(
                                'ownedSocial:socialPostMetrics.columns.paidImpressions',
                            )
                        ] = format.rawNumber(paidImpressions);
                        postRow[
                            t(
                                'ownedSocial:socialPostMetrics.columns.paidVideoViews',
                            )
                        ] = format.rawNumber(paidVideoViews);
                        postRow[
                            t(
                                'ownedSocial:socialPostMetrics.columns.paidEngagements',
                            )
                        ] = format.rawNumber(paidEngagements);
                        postRow[
                            `${t('ownedSocial:socialPostMetrics.columns.paidFullMediaValue')}-${currency}`
                        ] = format.rawNumber(paidFullMediaValue);
                    }

                    postRow[
                        `${t('ownedSocial:socialPostMetrics.columns.fullMediaValue')}-${currency}`
                    ] = format.rawNumber(FMV);

                    postsDataById[postId] = postRow;
                }

                return accRows;
            }, []);

            return [...acc, ...rows];
        }, []);

        const exposuresCsv = Papa.unparse(exposuresData, { delimiter });
        const postsCsv = Papa.unparse(Object.values(postsDataById), {
            delimiter,
        });

        downloadCSV(exposuresCsv, 'social-posts-exposures');
        downloadCSV(postsCsv, 'social-posts');

        sendNotification('Your CSV was downloaded successfully', {
            context: 'success',
        });
    } catch (e: any) {
        sendNotification(e?.message, { context: 'danger' });
    } finally {
        clearTimeout(openCSVDownloadModalTimeout);
        closeCSVDownloadModal();
    }
};
